import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Database, Loader2, ArrowDownToLine, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * PreviewSyncCard — one-tap "Sync Prod → Preview" for the admin dashboard.
 *
 * Why this exists: preview and production run on separate Mongo databases,
 * so preview accumulates newer campsites (22,307) faster than prod (21,999+).
 * Running the same ingest pipelines twice is slow and burns rate-limit budget.
 * This card pulls the preview snapshot directly into prod in one background
 * job, keyed on `campsite_id` so it's idempotent + safe to re-run daily.
 *
 * UX contract:
 *   - Editable source URL, remembered across sessions in localStorage.
 *   - Shows local vs source counts side-by-side with a "delta" pill.
 *   - Start button posts and immediately begins polling `/status` every 3s.
 *   - Live progress bar keyed on `fetched / source_total`.
 *   - Final banner shows inserted / updated / unchanged.
 */
const LS_URL_KEY = "cc:sync:source_url";
const POLL_MS = 3000;

const num = (v) => (typeof v === "number" ? v.toLocaleString() : "—");

export default function PreviewSyncCard() {
  const [sourceUrl, setSourceUrl] = useState("");
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null); // { local_total, sync }
  const [dryRun, setDryRun] = useState(false);
  const pollRef = useRef(null);

  // Load remembered URL on mount + initial status pull.
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(LS_URL_KEY);
      if (saved) setSourceUrl(saved);
    } catch {/* private mode — fail open */}
    refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/campsites/sync-from-preview/status");
      setStatus(data);
      // If a sync is running, keep polling. Otherwise stop.
      if (data?.sync?.status === "running") {
        if (!pollRef.current) pollRef.current = setInterval(refresh, POLL_MS);
      } else if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) {
      // 401/403 already handled by axios interceptor; silence others so
      // the card doesn't spam toasts every 3s during a network blip.
    }
  };

  const start = async () => {
    const trimmed = (sourceUrl || "").trim().replace(/\/+$/, "");
    if (!/^https?:\/\//.test(trimmed)) {
      toast.error("Enter a full URL starting with https://");
      return;
    }
    if (!window.confirm(
      dryRun
        ? "Dry-run: this will only count what WOULD change. No writes.\n\nContinue?"
        : "This will upsert every campsite from the source into THIS deploy's database.\n\nContinue?",
    )) return;
    setStarting(true);
    try {
      try { window.localStorage.setItem(LS_URL_KEY, trimmed); } catch {/* ignore */}
      const { data } = await api.post("/admin/campsites/sync-from-preview", {
        source_url: trimmed,
        dry_run: dryRun,
      });
      if (data?.status === "already_running") {
        toast.info("A sync is already in progress — polling for status.");
      } else {
        toast.success(`Sync started — source has ${num(data?.source_total)} campsites.`);
      }
      // Kick polling immediately.
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(refresh, POLL_MS);
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setStarting(false);
    }
  };

  const sync = status?.sync || {};
  const local = status?.local_total;
  const sourceTotal = sync.source_total;
  const running = sync.status === "running";
  const done = sync.status === "done";
  const err = sync.status === "error";
  const fetched = sync.fetched || 0;
  const pct = sourceTotal ? Math.min(100, Math.round((fetched / sourceTotal) * 100)) : 0;
  const delta = (typeof sourceTotal === "number" && typeof local === "number") ? sourceTotal - local : null;

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="preview-sync-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5 mb-1 text-[#ff4500]">
            <Database className="w-3 h-3" /> Data sync
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            Sync campsites from Preview → this deploy
          </h2>
          <p className="text-xs text-[#666] mt-1 max-w-2xl">
            Pulls every campsite from the source URL and upserts by <code className="mono text-[11px]">campsite_id</code>.
            Idempotent — safe to re-run. Skip if this <em>is</em> the preview deploy.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border hairline hover:border-[#ff4500] hover:bg-[#fff4ee] text-xs mono uppercase tracking-wider"
          data-testid="preview-sync-refresh"
          title="Refresh status"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-0 border hairline mb-4">
        <div className="p-3 border-r hairline">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#888] mb-1">This deploy</div>
          <div className="font-display text-2xl tabular-nums" data-testid="preview-sync-local-total">{num(local)}</div>
        </div>
        <div className="p-3 border-r hairline">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#888] mb-1">Source</div>
          <div className="font-display text-2xl tabular-nums" data-testid="preview-sync-source-total">{num(sourceTotal)}</div>
        </div>
        <div className="p-3">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#888] mb-1">Delta</div>
          <div className="font-display text-2xl tabular-nums" data-testid="preview-sync-delta">
            {delta === null ? "—" : (delta > 0 ? `+${num(delta)}` : num(delta))}
          </div>
        </div>
      </div>

      <label className="block mb-3">
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#666] mb-1 block">Source URL</span>
        <input
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://your-preview-deploy.preview.emergentagent.com"
          className="w-full px-3 py-2 rounded-xl border hairline bg-white text-sm mono focus:outline-none focus:border-[#ff4500]"
          data-testid="preview-sync-url-input"
        />
      </label>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <button
          type="button"
          onClick={start}
          disabled={starting || running || !sourceUrl.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="preview-sync-start"
        >
          {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowDownToLine className="w-3.5 h-3.5" />}
          {running ? "Syncing…" : (dryRun ? "Start dry-run" : "Sync now")}
        </button>
        <label className="inline-flex items-center gap-2 text-xs text-[#666] cursor-pointer">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            data-testid="preview-sync-dryrun"
          />
          Dry-run (count only, no writes)
        </label>
      </div>

      {(running || done || err) && (
        <div className="border hairline rounded-xl p-3 bg-[#fafafa]" data-testid="preview-sync-progress">
          {running && (
            <>
              <div className="flex items-center gap-2 mb-2 text-xs mono uppercase tracking-wider text-[#666]">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#ff4500]" />
                Running · {num(fetched)} / {num(sourceTotal)} fetched ({pct}%)
              </div>
              <div className="h-2 bg-white border hairline rounded overflow-hidden">
                <div
                  className="h-full bg-[#ff4500] transition-all"
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
              </div>
            </>
          )}
          {done && (
            <div className="text-xs text-[#065f46] flex items-start gap-2" data-testid="preview-sync-done">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold mb-0.5">
                  {sync.dry_run ? "Dry-run complete" : "Sync complete"}
                </div>
                <div className="mono text-[11px] text-[#0e0e0e]">
                  fetched {num(sync.fetched)} · inserted {num(sync.inserted)} · updated {num(sync.updated)} · unchanged {num(sync.unchanged)}
                </div>
              </div>
            </div>
          )}
          {err && (
            <div className="text-xs text-[#7f1d1d] flex items-start gap-2" data-testid="preview-sync-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold mb-0.5">Sync failed</div>
                <div className="mono text-[11px]">{sync.error || "Unknown error"}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
